import React, { ReactElement, useEffect, useState } from 'react'
import i18n from 'live/services/i18n'
import { STATIC_DATA_STATES, UnitsTypes } from '../../chart-components'
import { ModalButton, RestrictedContent, RestrictedMessage, RestrictedTitle } from '../../modal-components-style'
import { fetchEntityOptions, fetchKpiGoalsWithFilter, fetchUnitOptions, TUBULAR_API } from '../../../api/api'
import { RawActivityEvent, RestrictedConnection, SelectOptions, Uom, ValueUom } from '../../../utils/type'
import { RestrictedConnectionErrors, Errors, getEmptyRawRestrictedConnectionEvent } from '../types'
import { CONNECTION_TAB_ACRONYM, KPI_COLUMN, TUBULAR_TYPE_COLUMN } from '../constants'
import { getFormattedGoalLabel } from '../../../context/utils'
import { RestrictedConnectionInformation } from './ConnectionTap/RestrictedConnectionInformation'

interface ConnectionTabOptionsTemplate {
    restrictedConnectionType: SelectOptions[]
}

interface ConnectionTabProps {
    restrictedConnections: RestrictedConnection[]
    errors: RestrictedConnectionErrors
    validateRestrictedConnectionFields: (
        restrictedConnection: RestrictedConnection
    ) => { pass: boolean; errors: Errors }
    addRestrictedConnection: (restrictedConnection: RestrictedConnection) => void
    updateCurrentRestrictedConnection: (restrictedIndex: number, key: string, newValue: any) => void
    deleteRestrictedConnection: (index: number) => void
    tubularsIntervalPerTime: boolean
    tubularType: SelectOptions | null
    assetFilterId?: string
    setIsAddRestrictedConnectionButtonDisabled: (isDisabled: boolean) => void
    tubularEvent: RawActivityEvent
}

export const ConnectionTab = ({
    restrictedConnections,
    errors,
    validateRestrictedConnectionFields,
    addRestrictedConnection,
    updateCurrentRestrictedConnection,
    deleteRestrictedConnection,
    tubularsIntervalPerTime,
    tubularType,
    assetFilterId,
    setIsAddRestrictedConnectionButtonDisabled,
    tubularEvent
}: ConnectionTabProps): ReactElement => {
    const [lengthUnitOptions, setLengthUnitOptions] = useState<Uom[]>([])
    const [restrictionTypeList, setRestrictionTypeList] = useState<SelectOptions[]>([])
    const [isEventStarted, setIsEventStarted] = useState(
        tubularEvent.state === STATIC_DATA_STATES.STARTED_IN_EXECUTION ||
            tubularEvent.state === STATIC_DATA_STATES.FINISHED
    )
    const [emptyRawRestrictedConnectionEvent, setEmptyRawRestrictedConnectionEvent] = useState(
        getEmptyRawRestrictedConnectionEvent()
    )
    const [isAddRestrictedPerformanceButtonDisabled, setIsAddRestrictedPerformanceButtonDisabled] = useState<boolean>(
        false
    )

    const isTubularEmpty = tubularEvent?.state === STATIC_DATA_STATES.EMPTY

    const setDefaultUomMDUnit = (defaultLengthUnit: Uom) => {
        const defaultStartMD = { ...emptyRawRestrictedConnectionEvent.startMD }
        defaultStartMD.uom = defaultLengthUnit

        const defaultFinalMD = { ...emptyRawRestrictedConnectionEvent.startMD }
        defaultFinalMD.uom = defaultLengthUnit

        setEmptyRawRestrictedConnectionEvent(prev => ({ ...prev, startMD: defaultStartMD, finalMD: defaultFinalMD }))
    }

    const setDefaultUnits = () => {
        const defaultLengthUnit = lengthUnitOptions.find(elem => elem?.name == 'm') || lengthUnitOptions[0]

        setDefaultUomMDUnit(defaultLengthUnit)
    }

    useEffect(() => {
        fetchEntityOptions(TUBULAR_API).then((data: ConnectionTabOptionsTemplate) => {
            const hideUnrestrictedValue = data.restrictedConnectionType.filter(e => e.acronym != 'UN')
            setRestrictionTypeList(hideUnrestrictedValue)
        })
        fetchUnitOptions(UnitsTypes.LENGTH).then(r => {
            if (!r) return
            setLengthUnitOptions(r.options)
            const defaultLengthUnit = r.options.find(elem => elem.name == 'm') || r[0]

            setDefaultUomMDUnit(defaultLengthUnit)
        })
    }, [])

    useEffect(() => {
        setDefaultUnits()
    }, [lengthUnitOptions])

    useEffect(() => {
        setIsAddRestrictedConnectionButtonDisabled(isAddRestrictedPerformanceButtonDisabled)
    }, [isAddRestrictedPerformanceButtonDisabled])

    useEffect(() => {
        if (!restrictedConnections?.length) {
            setIsAddRestrictedPerformanceButtonDisabled(false)
        }
    }, [restrictedConnections])

    return (
        <>
            {isEventStarted && restrictedConnections?.length === 0 ? (
                <RestrictedMessage>
                    <i className="fa fa-empty-set" />
                    <RestrictedTitle>{i18n('No Restriction Applied')}</RestrictedTitle>
                    <RestrictedContent>
                        {i18n('There is no Restricted Performance defined for this tubular.')}
                    </RestrictedContent>
                </RestrictedMessage>
            ) : restrictedConnections?.length > 0 ? (
                <>
                    {restrictedConnections?.map((restrictedConnection, index) => (
                        <RestrictedConnectionInformation
                            restrictedConnection={restrictedConnection}
                            index={index}
                            tubularEvent={tubularEvent}
                            deleteRestrictedConnection={deleteRestrictedConnection}
                            errors={errors}
                            restrictionTypeList={restrictionTypeList}
                            updateCurrentRestrictedConnection={updateCurrentRestrictedConnection}
                            assetFilterId={assetFilterId}
                            tubularsIntervalPerTime={tubularsIntervalPerTime}
                            lengthUnitOptions={lengthUnitOptions}
                            setIsAddRestrictedPerformanceButtonDisabled={setIsAddRestrictedPerformanceButtonDisabled}
                            tubularType={tubularType}
                        />
                    ))}
                </>
            ) : (
                <>
                    <RestrictedMessage>
                        <i className="fa fa-empty-set" style={{ marginTop: '34px' }} />
                        <RestrictedTitle>No Restriction Applied</RestrictedTitle>
                        <RestrictedContent>
                            There is no Restricted Performance defined for this tubular. Click the Add Restricted button
                            to include.
                        </RestrictedContent>
                    </RestrictedMessage>
                </>
            )}
            {
                <ModalButton
                    styleType="action"
                    style={{ width: '100%' }}
                    disabled={isAddRestrictedPerformanceButtonDisabled}
                    onClick={() => {
                        const defaultLengthUnit =
                            lengthUnitOptions.find(elem => elem?.name == 'm') || lengthUnitOptions[0]
                        addRestrictedConnection(getEmptyRawRestrictedConnectionEvent(defaultLengthUnit))
                    }}
                >
                    <i style={{ paddingRight: '4px' }} className="far fa-plus-circle"></i>
                    {i18n('Add Restricted Connection')}
                </ModalButton>
            }
        </>
    )
}
